import React, { useEffect, useId, useRef, useState } from "react";

const COMPLETION_ENDPOINT = "https://data.geopf.fr/geocodage/completion/";
const SEARCH_ENDPOINT = "https://data.geopf.fr/geocodage/search";
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_DELAY = 300;
const MIN_REQUEST_INTERVAL = 350;
const MAX_SUGGESTIONS = 5;
const MAX_CACHE_ENTRIES = 50;
const REQUEST_TIMEOUT = 5500;

const AUTOCOMPLETE_UNAVAILABLE_MESSAGE =
  "L’autocomplétion est indisponible. Vous pouvez continuer en saisissant l’adresse manuellement.";
const NO_RESULT_MESSAGE =
  "Aucune adresse proposée. Vous pouvez continuer en saisissant l’adresse manuellement.";

function emptyAddressMetadata() {
  return {
    adresse_normalisee: "",
    code_postal: "",
    ville: "",
    code_insee: "",
    latitude: null,
    longitude: null,
  };
}

function cleanText(value) {
  if (typeof value !== "string" && typeof value !== "number") return "";
  return String(value).trim().replace(/\s+/g, " ");
}

function normalizeQuery(value) {
  return cleanText(value);
}

function queryCacheKey(value) {
  return normalizeQuery(value).toLocaleLowerCase("fr-FR");
}

function adaptCompletionResponse(payload) {
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const suggestions = [];
  const labels = new Set();

  for (const entry of results) {
    if (!entry || typeof entry !== "object") continue;

    const street = cleanText(entry.street) || cleanText(entry.name);
    const number = cleanText(entry.number) || cleanText(entry.housenumber);
    const city = cleanText(entry.city);
    const postcode = cleanText(entry.zipcode) || cleanText(entry.postcode);
    const fallbackLabel = [
      [number, street].filter(Boolean).join(" "),
      [postcode, city].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");
    const label =
      cleanText(entry.fulltext) || cleanText(entry.label) || cleanText(fallbackLabel);

    if (!label || labels.has(label)) continue;

    labels.add(label);
    suggestions.push({
      id: cleanText(entry.id) || `${label}-${suggestions.length}`,
      label,
    });

    if (suggestions.length === MAX_SUGGESTIONS) break;
  }

  return suggestions;
}

function adaptSearchResponse(payload) {
  const features = Array.isArray(payload?.features) ? payload.features : [];

  for (const feature of features) {
    if (!feature || typeof feature !== "object") continue;

    const properties =
      feature.properties && typeof feature.properties === "object" && !Array.isArray(feature.properties)
        ? feature.properties
        : null;
    const coordinates = feature.geometry?.coordinates;
    const hasValidCoordinates =
      Array.isArray(coordinates) &&
      Number.isFinite(coordinates[0]) &&
      Number.isFinite(coordinates[1]);

    if (!properties || !hasValidCoordinates) continue;

    return {
      adresse_normalisee: cleanText(properties.label) || cleanText(properties.name),
      code_postal: cleanText(properties.postcode) || cleanText(properties.postalcode),
      ville: cleanText(properties.city) || cleanText(properties.municipality),
      code_insee: cleanText(properties.citycode) || cleanText(properties.city_code),
      longitude: coordinates[0],
      latitude: coordinates[1],
    };
  }

  return null;
}

function storeCachedSuggestions(cache, key, suggestions) {
  if (!cache.has(key) && cache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    cache.delete(oldestKey);
  }
  cache.set(key, suggestions);
}

export default function AddressAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  onMetadataChange = () => {},
  error,
}) {
  const generatedId = useId().replace(/:/g, "");
  const inputId = `address-input-${generatedId}`;
  const listboxId = `address-suggestions-${generatedId}`;
  const rootRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const completionTimeoutRef = useRef(null);
  const geocodingTimeoutRef = useRef(null);
  const completionAbortRef = useRef(null);
  const geocodingAbortRef = useRef(null);
  const completionSequenceRef = useRef(0);
  const geocodingSequenceRef = useRef(0);
  const lastCompletionRequestAtRef = useRef(0);
  const cacheRef = useRef(new Map());
  const mountedRef = useRef(false);
  const suppressedCompletionQueryRef = useRef("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    mountedRef.current = true;

    function handleOutsidePointer(event) {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    }

    document.addEventListener("pointerdown", handleOutsidePointer);

    return () => {
      mountedRef.current = false;
      document.removeEventListener("pointerdown", handleOutsidePointer);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (completionTimeoutRef.current) clearTimeout(completionTimeoutRef.current);
      if (geocodingTimeoutRef.current) clearTimeout(geocodingTimeoutRef.current);
      completionAbortRef.current?.abort();
      geocodingAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const normalizedQuery = normalizeQuery(value);
    const cacheKey = queryCacheKey(normalizedQuery);

    completionSequenceRef.current += 1;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (completionTimeoutRef.current) {
      clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }
    completionAbortRef.current?.abort();
    completionAbortRef.current = null;
    setActiveIndex(-1);

    if (
      suppressedCompletionQueryRef.current &&
      cacheKey === suppressedCompletionQueryRef.current
    ) {
      suppressedCompletionQueryRef.current = "";
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      setStatusMessage("");
      return undefined;
    }

    if (normalizedQuery.replace(/\s/g, "").length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      setStatusMessage("");
      return undefined;
    }

    if (cacheRef.current.has(cacheKey)) {
      const cachedSuggestions = cacheRef.current.get(cacheKey);
      setSuggestions(cachedSuggestions);
      setIsOpen(cachedSuggestions.length > 0);
      setIsLoading(false);
      setStatusMessage(cachedSuggestions.length ? "" : NO_RESULT_MESSAGE);
      return undefined;
    }

    setSuggestions([]);
    setIsOpen(false);
    setIsLoading(false);
    setStatusMessage("");

    const requestSequence = completionSequenceRef.current;

    async function requestSuggestions() {
      const elapsedSinceLastRequest = Date.now() - lastCompletionRequestAtRef.current;
      const remainingInterval = Math.max(0, MIN_REQUEST_INTERVAL - elapsedSinceLastRequest);

      if (remainingInterval > 0) {
        debounceTimerRef.current = setTimeout(requestSuggestions, remainingInterval);
        return;
      }

      const controller = new AbortController();
      let didTimeout = false;
      completionAbortRef.current = controller;
      lastCompletionRequestAtRef.current = Date.now();
      setIsLoading(true);

      const timeoutId = setTimeout(() => {
        didTimeout = true;
        controller.abort();
      }, REQUEST_TIMEOUT);
      completionTimeoutRef.current = timeoutId;

      try {
        const url = new URL(COMPLETION_ENDPOINT);
        url.search = new URLSearchParams({
          text: normalizedQuery,
          type: "StreetAddress",
          maximumResponses: String(MAX_SUGGESTIONS),
        }).toString();

        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error("completion-unavailable");

        const nextSuggestions = adaptCompletionResponse(await response.json());
        if (
          !mountedRef.current ||
          controller.signal.aborted ||
          requestSequence !== completionSequenceRef.current
        ) {
          return;
        }

        storeCachedSuggestions(cacheRef.current, cacheKey, nextSuggestions);
        setSuggestions(nextSuggestions);
        setIsOpen(nextSuggestions.length > 0);
        setStatusMessage(nextSuggestions.length ? "" : NO_RESULT_MESSAGE);
      } catch (requestError) {
        if (
          !mountedRef.current ||
          requestSequence !== completionSequenceRef.current ||
          (requestError?.name === "AbortError" && !didTimeout)
        ) {
          return;
        }

        setSuggestions([]);
        setIsOpen(false);
        setStatusMessage(AUTOCOMPLETE_UNAVAILABLE_MESSAGE);
      } finally {
        clearTimeout(timeoutId);
        if (completionTimeoutRef.current === timeoutId) {
          completionTimeoutRef.current = null;
        }
        if (
          mountedRef.current &&
          requestSequence === completionSequenceRef.current
        ) {
          setIsLoading(false);
        }
        if (completionAbortRef.current === controller) {
          completionAbortRef.current = null;
        }
      }
    }

    debounceTimerRef.current = setTimeout(requestSuggestions, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [value]);

  function handleManualChange(event) {
    suppressedCompletionQueryRef.current = "";
    geocodingSequenceRef.current += 1;
    if (geocodingTimeoutRef.current) {
      clearTimeout(geocodingTimeoutRef.current);
      geocodingTimeoutRef.current = null;
    }
    geocodingAbortRef.current?.abort();
    geocodingAbortRef.current = null;
    setIsOpen(false);
    setActiveIndex(-1);
    setStatusMessage("");
    onMetadataChange(emptyAddressMetadata());
    onChange(event.target.value);
  }

  async function selectSuggestion(suggestion) {
    const selectedLabel = suggestion.label;
    suppressedCompletionQueryRef.current = queryCacheKey(selectedLabel);
    completionSequenceRef.current += 1;
    completionAbortRef.current?.abort();
    completionAbortRef.current = null;
    setSuggestions([]);
    setIsOpen(false);
    setActiveIndex(-1);
    setIsLoading(false);
    setStatusMessage("");
    onMetadataChange(emptyAddressMetadata());
    onChange(selectedLabel);

    geocodingSequenceRef.current += 1;
    const requestSequence = geocodingSequenceRef.current;
    if (geocodingTimeoutRef.current) {
      clearTimeout(geocodingTimeoutRef.current);
      geocodingTimeoutRef.current = null;
    }
    geocodingAbortRef.current?.abort();
    const controller = new AbortController();
    let didTimeout = false;
    geocodingAbortRef.current = controller;

    const timeoutId = setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, REQUEST_TIMEOUT);
    geocodingTimeoutRef.current = timeoutId;

    try {
      const url = new URL(SEARCH_ENDPOINT);
      url.search = new URLSearchParams({
        q: selectedLabel,
        limit: "1",
      }).toString();

      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error("search-unavailable");

      const metadata = adaptSearchResponse(await response.json());
      if (!metadata) throw new Error("search-invalid-response");
      if (
        !mountedRef.current ||
        controller.signal.aborted ||
        requestSequence !== geocodingSequenceRef.current
      ) {
        return;
      }

      const visibleAddress = metadata.adresse_normalisee || selectedLabel;
      if (visibleAddress !== selectedLabel) {
        suppressedCompletionQueryRef.current = queryCacheKey(visibleAddress);
        onChange(visibleAddress);
      }
      onMetadataChange(metadata);
    } catch (requestError) {
      if (
        !mountedRef.current ||
        requestSequence !== geocodingSequenceRef.current ||
        (requestError?.name === "AbortError" && !didTimeout)
      ) {
        return;
      }

      onMetadataChange(emptyAddressMetadata());
      setStatusMessage(AUTOCOMPLETE_UNAVAILABLE_MESSAGE);
    } finally {
      clearTimeout(timeoutId);
      if (geocodingTimeoutRef.current === timeoutId) {
        geocodingTimeoutRef.current = null;
      }
      if (geocodingAbortRef.current === controller) {
        geocodingAbortRef.current = null;
      }
    }
  }

  function handleKeyDown(event) {
    if (event.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!suggestions.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((currentIndex) =>
        currentIndex >= suggestions.length - 1 ? 0 : currentIndex + 1
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((currentIndex) =>
        currentIndex <= 0 ? suggestions.length - 1 : currentIndex - 1
      );
      return;
    }

    if (event.key === "Enter" && isOpen && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
    }
  }

  const expanded = isOpen && suggestions.length > 0;
  const activeOptionId =
    expanded && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;

  return (
    <div className="field address-field" ref={rootRef}>
      <label className="field-label" htmlFor={inputId}>
        {label}
      </label>
      <div className="address-input address-autocomplete">
        <input
          id={inputId}
          className={`input ${error ? "has-error" : ""}`}
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={handleManualChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length) setIsOpen(true);
          }}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-invalid={Boolean(error)}
          aria-describedby={
            error
              ? `${inputId}-status ${inputId}-error`
              : `${inputId}-status`
          }
        />

        {expanded ? (
          <ul id={listboxId} className="address-suggestions" role="listbox">
            {suggestions.map((suggestion, index) => {
              const optionId = `${listboxId}-option-${index}`;
              const isActive = index === activeIndex;
              return (
                <li
                  id={optionId}
                  key={`${suggestion.id}-${index}`}
                  className={`address-suggestion ${isActive ? "is-active" : ""}`}
                  role="option"
                  aria-selected={isActive}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  {suggestion.label}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      <span
        id={`${inputId}-status`}
        className={`address-autocomplete-status ${
          statusMessage === AUTOCOMPLETE_UNAVAILABLE_MESSAGE ? "is-unavailable" : ""
        }`}
        role="status"
        aria-live="polite"
      >
        {isLoading ? "Recherche d’adresses…" : statusMessage}
      </span>
      {error && (
        <span id={`${inputId}-error`} className="error">
          {error}
        </span>
      )}
    </div>
  );
}
